import { FileTypeEnum, GetGenericListCommandDto } from 'api/actor';
import { DocumentScopeEnum, DocumentViewItem, GetDocumentViewItemsResponse } from 'api/documents';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import {
  FileText,
  Download,
  Eye,
  Trash2,
  Edit,
  Shield,
  FileSpreadsheet,
  Image as ImageIcon,
  File
} from 'lucide-react';
import { documentsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import { FetchDataParams, TableResponse } from 'slices/generic-table/genericTableSlice';

export const fileTypeLabels: Record<FileTypeEnum, string> = {
  [FileTypeEnum.PhotoBienImmobilier]: "Photo du bien immobilier",
  [FileTypeEnum.IconeGarantieTunnel]: "Icône garantie tunnel",
  [FileTypeEnum.DocumentLocataire]: "Document locataire",
  [FileTypeEnum.PhotoAssure]: "Photo assuré",
  [FileTypeEnum.Document]: "Document",
  [FileTypeEnum.DocumentGenerer]: "Document généré",
  [FileTypeEnum.DocumentSigner]: "Document signé",
  [FileTypeEnum.ThemeLogo]: "Logo thème",
  [FileTypeEnum.SimulationPdfGenerer]: "Simulation PDF",
  [FileTypeEnum.DevisPdfGenerer]: "Devis PDF",
  [FileTypeEnum.ContratPdfGenerer]: "Contrat PDF",
  [FileTypeEnum.AdviceSheetPdfGenerer]: "Fiche conseil PDF",
  [FileTypeEnum.OriasPreuve]: "Preuve ORIAS",
  [FileTypeEnum.RcpCertificat]: "Certificat RCP",
  [FileTypeEnum.AttestationAssurance]: "Attestation d'assurance",
  [FileTypeEnum.AttestationSinistre]: "Attestation de sinistre",
  [FileTypeEnum.AvenantPdfGenerer]: "Avenant PDF",
  [FileTypeEnum.ExportCSV]: "Export CSV",
  [FileTypeEnum.BordereauCommissionCSV]: "Bordereau de commission CSV",
  [FileTypeEnum.ContratGroupePdfGenerer]: "Contrat de groupe PDF",
  [FileTypeEnum.CourtierPresentation]: "Présentation courtier",
  [FileTypeEnum.ConditionsParticulieres]: "Conditions particulières",
  [FileTypeEnum.QuittanceGenerer]: "Quittance",
  [FileTypeEnum.CertificatAdhesion]: "Certificat d'adhésion",
  [FileTypeEnum.ContratSigne]: "Contrat signé",
  [FileTypeEnum.PreuveSignature]: "Preuve de signature",
  [FileTypeEnum.BulletinAdhesion]: "Bulletin d'adhésion",
  [FileTypeEnum.FicheProduit]: "Fiche produit",
  [FileTypeEnum.Ipid]: "IPID",
  [FileTypeEnum.Notice]: "Notice / Conditions générales",
  [FileTypeEnum.SimulationComplete]: "Simulation complète",
  [FileTypeEnum.BulletinAdhesionSigne]: "Bulletin d'adhésion signé",
  [FileTypeEnum.ContratTravail]: "Contrat de travail",
  [FileTypeEnum.AttestationEmployeur]: "Attestation employeur",
  [FileTypeEnum.BulletinSalaireM1]: "Bulletin de salaire M-1",
  [FileTypeEnum.AvisImposition]: "Avis d'imposition",
  [FileTypeEnum.AttestationRessources]: "Attestation de ressources",
  [FileTypeEnum.DecompteRetraite]: "Décompte retraite",
  [FileTypeEnum.CarteEtudiant]: "Carte étudiant",
  [FileTypeEnum.CertificatScolarite]: "Certificat de scolarité",
  [FileTypeEnum.ContratApprentissage]: "Contrat d'apprentissage",
  [FileTypeEnum.TitreSejour]: "Titre de séjour",
  [FileTypeEnum.PieceIdentite]: "Pièce d'identité",
  [FileTypeEnum.ConditionsSpeciales]: "Conditions spéciales",
  [FileTypeEnum.JustificatifDomicile]: "Justificatif de domicile",
  [FileTypeEnum.JustificatifAideSociale]: "Justificatif d'aide sociale",
  [FileTypeEnum.BulletinSalaireM2]: "Bulletin de salaire M-2",
  [FileTypeEnum.BulletinSalaireM3]: "Bulletin de salaire M-3",
  [FileTypeEnum.PieceIdentiteLocataire]: "Pièce d'identité du locataire",
  [FileTypeEnum.QuestionnaireSante]: "Questionnaire de santé",
  [FileTypeEnum.BonPourAccord]: "Bon pour accord",
  [FileTypeEnum.QuestionnaireSanteSigne]: "Questionnaire de santé signé",
  [FileTypeEnum.BonPourAccordSigne]: "Bon pour accord signé",
  [FileTypeEnum.LogoPlateforme]: "Logo plateforme",
  [FileTypeEnum.LogoMinimalPlateforme]: "Logo minimaliste plateforme",
  [FileTypeEnum.LogoProduit]: "Logo du produit",
  [FileTypeEnum.Rib]: "RIB",
  [FileTypeEnum.MandatSepa]: "Mandat SEPA",
  [FileTypeEnum.Ppe]: "PPE",
  [FileTypeEnum.Aeras]: "AERAS",
  [FileTypeEnum.Fsi]: "FSI",
  [FileTypeEnum.QuestionnaireSanteRetourMoteur]: "Questionnaire de santé - Retour moteur",
  [FileTypeEnum.Kbis]: "Extrait KBIS",
  [FileTypeEnum.ResultatFicheMedicale]: "Résultat fiche médicale",
  [FileTypeEnum.MandatSubstitutionAssurance]: "Mandat de substitution d'assurance",
  [FileTypeEnum.MandatSubstitutionAssuranceSigne]: "Mandat de substitution d'assurance signé",
  [FileTypeEnum.CourrierSubstitutionAssurance]: "Courrier de demande de substitution",
  [FileTypeEnum.OffreDePret]: "Offre de prêt",
  [FileTypeEnum.TableauAmortissement]: "Tableau d'amortissement",
  [FileTypeEnum.LiasseBanqueSubstitution]: "Liasse banque - Demande de substitution",
  [FileTypeEnum.ReleveInformationSinistralite]: "Relevé d'information de sinistralité",
};

const scopeLabels: Record<DocumentScopeEnum, string> = {
  [DocumentScopeEnum.Quote]: "Devis",
  [DocumentScopeEnum.Contract]: "Contrat",
  [DocumentScopeEnum.Product]: "Produit",
  [DocumentScopeEnum.Amendment]: "Avenant",
  [DocumentScopeEnum.Receipt]: "Quittance",
  [DocumentScopeEnum.Broker]: "Courtier",
  [DocumentScopeEnum.System]: "Système",
  [DocumentScopeEnum.Tenant]: "Locataire",
  [DocumentScopeEnum.Subscriber]: "Souscripteur",
  [DocumentScopeEnum.BorrowerProject]: "Autres documents"
};

const statusColors: Record<string, string> = {
  'PENDING_VALIDATION': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'VALIDATED': 'bg-green-100 text-green-800 border-green-200',
  'REJECTED': 'bg-red-100 text-red-800 border-red-200',
  'EXPIRED': 'bg-orange-100 text-orange-800 border-orange-200',
  'ARCHIVED': 'bg-gray-100 text-gray-800 border-gray-200'
};

// ============================================
// UTILITAIRES
// ============================================

const getFileIcon = (mimetype: string) => {
  return <FileText className="text-red-500" size={20} />
};

const formatDate = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const isExpired = (date: Date | string) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d < new Date();
};

const isExpiringSoon = (date: Date | string, daysThreshold = 30) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + daysThreshold);
  return d <= threshold;
};

// ============================================
// API CALLS (remplacer les mocks par vos vrais endpoints)
// ============================================

const fetchDocuments = async (params: FetchDataParams): Promise<TableResponse<DocumentViewItem>> => {
  try {
    const command: GetGenericListCommandDto = {
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
      searchString: params.searchString || undefined,
      sorting: params.sorting as any,
    };

    const result: GetDocumentViewItemsResponse = await documentsAPI.getDocuments({
      getGenericListCommandDto: command
    });

    return {
      items: result.items,
      totalItems: result.totalCount,
      totalPages: Math.ceil((result.totalCount) / params.pageSize),
      currentPage: params.pageNumber
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des assures:', error);
    throw error;
  }
}

const fetchFilters = async () => {
  // TODO: Remplacer par votre vrai appel API pour récupérer les filtres
  // const response = await apiClient.get('/documents/filters');
  // return response.data;

  return [
    {
      groupName: 'Type de fichier',
      filters: Object.entries(fileTypeLabels).map(([key, label]) => ({
        key: 'fileType',
        label,
        type: 'select' as const,
        options: [{ value: key, label }]
      }))
    },
    {
      groupName: 'Portée',
      filters: Object.entries(scopeLabels).map(([key, label]) => ({
        key: 'scope',
        label,
        type: 'select' as const,
        options: [{ value: key, label }]
      }))
    },
    {
      groupName: 'Statut',
      filters: [
        { key: 'status', label: 'En attente', type: 'select' as const, options: [{ value: 'PENDING_VALIDATION', label: 'En attente' }] },
        { key: 'status', label: 'Validé', type: 'select' as const, options: [{ value: 'VALIDATED', label: 'Validé' }] },
        { key: 'status', label: 'Rejeté', type: 'select' as const, options: [{ value: 'REJECTED', label: 'Rejeté' }] },
        { key: 'status', label: 'Expiré', type: 'select' as const, options: [{ value: 'EXPIRED', label: 'Expiré' }] },
        { key: 'status', label: 'Archivé', type: 'select' as const, options: [{ value: 'ARCHIVED', label: 'Archivé' }] }
      ]
    }
  ];
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function DocumentsTable() {
  // Actions sur les documents
  const handleView = (doc: DocumentViewItem) => {
    console.log('Voir:', doc.name);
    // TODO: Implémenter la logique de visualisation
  };

  return (
    <SimpleDashboardContent>
      <DashboardHeader />
      <ServerTable<DocumentViewItem>
        // Configuration API
        apiConfig={{
          fetchData: fetchDocuments,
          fetchFilters: fetchFilters
        }}

        // Colonnes
        columns={[
          {
            key: 'name',
            label: 'Nom du document',
            sortable: true,
            width: '300px',
            render: (value: any, row: DocumentViewItem) => (
              <div className="flex items-center gap-3">
                {getFileIcon(row.mimetype)}
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900 truncate max-w-xs">
                    {String(value)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {row.mimetype}
                  </span>
                </div>
              </div>
            )
          },
          {
            key: 'fileType',
            label: 'Type de fichier',
            sortable: true,
            render: (value: any) => (
              <div className="flex items-center gap-2">
                <Shield className="text-primary" size={16} />
                <span className="text-sm font-medium text-gray-700">
                  {fileTypeLabels[value as FileTypeEnum]}
                </span>
              </div>
            )
          },
          {
            key: 'scope',
            label: 'Portée',
            sortable: true,
            render: (value: any) => (
              <span className="text-sm text-gray-700">
                {scopeLabels[value as DocumentScopeEnum]}
              </span>
            )
          },
          {
            key: 'status',
            label: 'Statut',
            sortable: true,
            align: 'center',
            render: (value: any, row: DocumentViewItem) => (
              <div className="flex flex-col items-center gap-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[value as string] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                  {String(value)}
                </span>
                {row.validationDate && (
                  <span className="text-xs text-gray-500">
                    Validé le {formatDate(row.validationDate)}
                  </span>
                )}
              </div>
            )
          },
          {
            key: 'subscriberName',
            label: 'Souscripteur',
            sortable: true,
            render: (value: any) => (
              <span className="text-sm text-gray-900">{String(value)}</span>
            )
          },
          {
            key: 'uploadDate',
            label: 'Date d\'upload',
            sortable: true,
            render: (value: any) => (
              <div className="flex flex-col">
                <span className="text-sm text-gray-900">
                  {formatDate(value as Date)}
                </span>
                <span className="text-xs text-gray-500">
                  {(value as Date).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            )
          },
          {
            key: 'tags',
            label: 'Tags',
            render: (value: any) => {
              const tags = value as string[];
              return (
                <div className="flex flex-wrap gap-1">
                  {tags.length > 0 ? (
                    tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">—</span>
                  )}
                  {tags.length > 3 && (
                    <span className="text-xs text-gray-500">+{tags.length - 3}</span>
                  )}
                </div>
              );
            }
          }
        ]}

        // En-tête
        title={{
          label: 'Mes documents',
          iconKey: 'file-text'
        }}

        // Actions
        actions={{
          create: {
            label: 'Importer un document',
            onClick: () => { }
          },
          row: [
            {
              icon: <Eye size={16} />,
              onClick: handleView,
              variant: 'primary'
            },
          ],
          bulk: [
            {
              label: 'Télécharger la sélection',
              icon: <Download size={16} />,
              onClick: () => { }
            },
            {
              label: 'Supprimer la sélection',
              icon: <Trash2 size={16} />,
              onClick: () => { },
              variant: 'danger'
            }
          ]
        }}

        // Configuration
        selectable
        pageSize={15}
        pageSizeOptions={[10, 15, 20, 30, 50]}
        showPaginationInfo

        // Recherche et filtres
        enableSearch
        enableFilters
        searchPlaceholder="Rechercher un document (nom, type, souscripteur...)"

        // Skeleton
        skeletonRowCount={10}

        // Callbacks
        onRowClick={handleView}
      />
    </SimpleDashboardContent>
  );
}
